import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import { INSIGHT_TONE_DOT_CLASSES } from "@/v2-backoffice-client/usage/usage.constants"
import UsageInsightText from "@/v2-backoffice-client/usage/UsageInsightText"
import type { UsageInsightMention } from "@/v2-backoffice-client/usage/scope/usage.scope.insights"

export type UsageReportFinding = {
  key: string
  /**
   * A bold lead-in naming the move ("Activate idle seats") in front of the
   * sentence, so a card of several findings can be triaged at a glance.
   */
  label?: string
  text: string
  /**
   * What kind of statement it is: something to act on (amber), an opening
   * (teal), good news (green), or a plain fact (neutral). The dot in front
   * of the sentence carries it; the sentence itself stays prose.
   */
  tone?: "action" | "opportunity" | "positive" | "neutral"
  /** Users named in `text`, rendered as links into their panel. */
  mentions?: UsageInsightMention[]
}

export type UsageReportFindingsProps = ComponentProps<"ul"> & {
  findings: UsageReportFinding[]
  /**
   * Deep link per mention; contexts differ (a page uses `?v=user:<id>`,
   * an open panel `>>?v=user:<id>` so the user sheet pushes onto the stack).
   */
  buildMentionTo?(mention: UsageInsightMention): string
}

/**
 * "Key findings": the report's prose bullets. Each is a full sentence
 * derived from the numbers on the page, so a reader gets the story before
 * the tables, and a sales or CS person can lift a line straight into a
 * customer conversation.
 */
function UsageReportFindings(props: UsageReportFindingsProps) {
  const { findings, buildMentionTo, className, ...ulProps } = props

  if (findings.length === 0) return null

  return (
    <ul className={cn("usage-report-findings", className)} {...ulProps}>
      {findings.map((finding) => (
        <li key={finding.key} className="usage-report-finding">
          <span
            aria-hidden
            className={cn(
              "usage-report-finding-dot",
              INSIGHT_TONE_DOT_CLASSES[finding.tone ?? "neutral"],
            )}
          />
          <p>
            {finding.label != null && (
              <strong className="usage-report-finding-label">
                {finding.label}.
              </strong>
            )}
            {buildMentionTo ? (
              <UsageInsightText
                text={finding.text}
                mentions={finding.mentions}
                buildTo={buildMentionTo}
              />
            ) : (
              finding.text
            )}
          </p>
        </li>
      ))}
    </ul>
  )
}

export default UsageReportFindings
